/**
 * Posts mode of `/content` (SPEC `Content Performance` § Posts mode, endpoint #88).
 *
 * `ContentTable` owns the grid; this file owns the three things only the posts table knows: its
 * column defs, its endpoint, and what a row click means.
 *
 * A click opens the shared `?post=` popup — but only for a row scraping already tracks. `postId` is
 * the server's discriminant for that, and the `ids` handed to the popup are the **enriched rows
 * only**, in the grid's current order: prev/next walks the posts the app can actually show, so a
 * gap of untracked exports between two tracked ones is skipped rather than stepping into a dialog
 * with nothing to load. An untracked row is inert; its cell carries the link to LinkedIn instead.
 *
 * The layout hook is called here *and* in the header's Columns picker: it is cache-backed, so the
 * two instances are the same layout, never two copies of it.
 */

import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  CONTENT_POSTS_SORT_KEYS,
  CONTENT_POSTS_COLUMN_IDS,
  contentPostsSortKeyOr,
  getContentPostsColumn,
  type ContentPostRowDTO,
  type ContentPostsQuery,
} from '@lw/shared';
import { getContentPosts } from '../../api/content';
import { QK } from '../../api/queryKeys';
import { EmptyState } from '../../components';
import { usePostPreview } from '../view/PreviewContext';
import { ContentTable, type ContentTableWindow } from './ContentTable';
import { buildContentPostsColumns, contentPostsColumnWidth, CONTENT_POSTS_FLEX_COLUMN } from './contentColumns';
import { CONTENT_POSTS_LAYOUT, useContentColumnLayout } from './useContentColumnLayout';
import type { ContentPostsSort } from './contentState';

export type ContentPostsTableProps = {
  /** #88 params minus the window: range, search, sort. */
  params: Partial<ContentPostsQuery>;
  /** §9's media toggle — a thumbnail is never requested while media is off. */
  mediaEnabled: boolean;
  sort: ContentPostsSort;
  onSortChange: (next: ContentPostsSort) => void;
  enabled: boolean;
  /** `true` when a search term or a non-default range could be what emptied the grid. */
  narrowed: boolean;
  onClear: () => void;
  footerLeft: ReactNode;
};

export function ContentPostsTable({
  params,
  mediaEnabled,
  sort,
  onSortChange,
  enabled,
  narrowed,
  onClear,
  footerLeft,
}: ContentPostsTableProps) {
  const layout = useContentColumnLayout(CONTENT_POSTS_LAYOUT);
  const { openPreview } = usePostPreview();
  const columnDefs = useMemo(() => buildContentPostsColumns({ mediaEnabled }), [mediaEnabled]);

  return (
    <ContentTable<ContentPostRowDTO, (typeof CONTENT_POSTS_COLUMN_IDS)[number]>
      getColumn={getContentPostsColumn}
      allIds={CONTENT_POSTS_COLUMN_IDS}
      columnDefs={columnDefs}
      columnWidth={contentPostsColumnWidth}
      flexColumnId={CONTENT_POSTS_FLEX_COLUMN}
      layout={layout}
      sort={sort}
      // `ContentTable` only ever emits a sortable id (it guards on `sortableKeys`); the narrowing
      // is what proves that to the type system rather than a cast.
      onSortChange={(next) =>
        onSortChange({ key: contentPostsSortKeyOr(next.key, sort.key), dir: next.dir })
      }
      sortableKeys={CONTENT_POSTS_SORT_KEYS}
      enabled={enabled}
      queryKeyFor={(window: ContentTableWindow) => QK.contentPosts({ ...params, ...window })}
      fetchPage={(window, signal) => getContentPosts({ ...params, ...window }, signal)}
      onRowClick={(row, rows) => {
        if (row.postId === null) return;
        const trackedIds = rows
          .map((entry) => entry.postId)
          .filter((postId): postId is number => postId !== null);
        openPreview(row.postId, trackedIds);
      }}
      empty={
        narrowed ? (
          <EmptyState
            title="No posts match"
            description="Every imported post was filtered out. Clear the search and widen the range to see the full list."
            action={
              <button type="button" className="btn-primary" onClick={onClear}>
                Clear
              </button>
            }
          />
        ) : (
          <EmptyState
            title="No posts in this range"
            description="The imports you have stored cover no post published in this range."
          />
        )
      }
      errorTitle="Couldn't load the imported posts"
      footerLeft={footerLeft}
    />
  );
}

export default ContentPostsTable;
